<template>
  <div
    class="position-relative bg-white"
    style="border-radius: 28px;"
  >
    <div class="pt-2 px-2">
      <div class="d-flex align-items-start justify-content-between">
        <div>
          <h4 class="font-medium text-primary">
            {{ $t('campaign.duplicateCampaign.titleModal') }}
          </h4>
          <p class="mb-0 text-placeholder">
            {{ $t('campaign.duplicateCampaign.textReview') }}
          </p>
        </div>
        <b-dropdown
          variant="link"
          toggle-class="p-0"
          no-caret
          right
        >
          <template v-slot:button-content>
            <btn-loading
              variant-convert="btn-submit"
              pill
              class="d-flex align-items-center"
            >
              <span>Bulk edit</span>
              <feather-icon
                icon="ChevronDownIcon"
                size="16"
                class="ml-50"
              />
            </btn-loading>
          </template>
          <b-dropdown-item
            :disabled="getNestedValue(campaigns[0], ['adgroupList', 0, 'deepBidType']) !== 'VO_MIN_ROAS' && getNestedValue(campaigns[0], ['adgroupList', 0, 'bidType']) !== 'BID_TYPE_CUSTOM'"
            @click="openBulkEditBidModal(convertTextBid(getNestedValue(campaigns[0], ['adgroupList', 0])).label)"
          >
            <span>{{ convertTextBid(getNestedValue(campaigns[0], ['adgroupList', 0])).label }}</span>
          </b-dropdown-item>
          <b-dropdown-item
            :disabled="!isAdGroupSameBudgetMode && isAdgroupBudgetTotal"
            @click="openBulkEditModal('schedule time')"
          >
            <span>Schedule Time</span>
          </b-dropdown-item>
        </b-dropdown>
      </div>
      <div
        v-if="initLoading"
        class="d-flex justify-content-center align-items-center border rounded my-2"
        style="height: 200px"
      >
        <b-spinner
          variant="secondary"
          small
        />
      </div>
      <validation-observer
        ref="campaignSetupValidation"
      >
        <div
          v-for="(campaignItem, index) in campaigns"
          :key="index"
        >
          <b-form class="form mx-auto my-2 p-2 border rounded">
            <div class="d-flex justify-content-between">
              <div
                class="d-flex"
                style="width: 70%;"
              >
                <feather-icon
                  v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                  v-b-toggle="`collapse-${index}-inner`"
                  :icon="collapseStates[index] ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                  size="21"
                  class="mr-1"
                  style="min-width: max-content"
                />
                <b-form-group
                  class="mb-0 position-relative font-20"
                  style="width: 100%;"
                >
                  <validation-provider
                    #default="{ errors }"
                    :name="$t('campaign.duplicateCampaign.textNumberOfDuplicates')"
                    rules="required"
                  >
                    <!-- Dynamic Field Editor Component -->
                    <dynamic-field-editor
                      :value="campaignItem.campaignName"
                      :is-editing="isFieldEditing(index, ['campaignName'])"
                      :errors="errors"
                      :field-name="$t('campaign.duplicateCampaign.textNumberOfDuplicates')"
                      @toggle-edit="toggleFieldEdit(index, ['campaignName'], campaignItem.campaignName)"
                      @cancel-edit="cancelFieldEdit(index, ['campaignName'])"
                      @input="updateFieldValue(index, ['campaignName'], $event)"
                    />
                  </validation-provider>
                </b-form-group>
              </div>

              <div
                class="mr-2"
                style="width: 20%;"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Budget"
                  :rules="`${campaignItem.budgetMode !== 'BUDGET_MODE_INFINITE' ? 'required|between:20,9999999' : ''}`"
                >
                  <label class="form-label">
                    Budget
                  </label>
                  <b-input-group v-if="campaignItem.budgetMode !== 'BUDGET_MODE_INFINITE'">
                    <b-form-input
                      :value="campaignItem.budget"
                      name="budgetCampaign"
                      placeholder="Enter budget"
                      style="height: 38px"
                      :state="errors.length ? false : null"
                      @input="updateFieldValue(index, ['budget'], $event)"
                    />
                  </b-input-group>
                  <b-input-group v-else>
                    <b-form-input
                      :value="'All'"
                      disabled
                      name="campaignBudget"
                      style="height: 38px"
                      :state="errors.length ? false : null"
                    />
                  </b-input-group>
                  <small
                    v-if="errors[0]"
                    class="text-danger d-block mt-25"
                  >{{ errors[0] }}</small>
                  <p class="mb-0 mt-50 font-14 text-placeholder">
                    {{ getBudgetType(campaignItem.budgetMode) }}
                  </p>
                </validation-provider>
              </div>

              <div
                v-b-tooltip.hover.top.v-light="'Delete'"
                class="rounded-circle d-flex align-items-center justify-content-center"
                :class="campaigns.length === 1 ? 'cursor-not-allowed' : 'cursor-pointer'"
                style="background-color: rgba(225, 28, 78, 0.1); width: 32px; height: 32px;"
              >
                <feather-icon
                  icon="Trash2Icon"
                  size="21"
                  class="text-danger"
                  @click="handleDeleteCampaign(index)"
                />
              </div>
            </div>

            <b-collapse
              v-model="collapseStates[index]"
              :id="`collapse-${index}-inner`"
            >
              <div
                v-if="campaignItem.adgroupList && campaignItem.adgroupList.length > 0"
                class="mt-2"
              >
                <h6 class="mb-1">
                  Ad Groups ({{ campaignItem.adgroupList.length }})
                </h6>
                <b-row
                  v-for="(adgroup, adgroupIndex) in campaignItem.adgroupList"
                  :key="adgroupIndex"
                  class="d-flex justify-content-between mt-1 flex-nowrap pb-1"
                  style="overflow-x: scroll"
                >
                  <b-col
                    cols="12"
                    md="3"
                  >
                    <!-- Ad Group Name -->
                    <validation-provider
                      #default="{ errors }"
                      name="Ad Group Name"
                      rules="required"
                    >
                      <label class="form-label">
                        Ad Group Name
                      </label>
                      <b-input-group>
                        <b-form-input
                          :value="getNestedValue(campaignItem, ['adgroupList', adgroupIndex, 'adgroupName'])"
                          name="adgroupName"
                          placeholder="Enter ad group name"
                          style="height: 38px"
                          :state="errors.length ? false : null"
                          @input="updateFieldValue(index, ['adgroupList', adgroupIndex, 'adgroupName'], $event)"
                        />
                      </b-input-group>
                      <small
                        v-if="errors[0]"
                        class="text-danger d-block mt-25"
                      >{{ errors[0] }}</small>
                    </validation-provider>
                  </b-col>

                  <b-col
                    cols="12"
                    md="3"
                    lg="2"
                  >
                    <!-- ROAS Bid -->
                    <!-- VALUE + VO_MIN_ROAS => Target ROAS -->
                    <validation-provider
                      v-if="['VALUE', 'TRAFFIC_LANDING_PAGE_VIEW'].includes(getNestedValue(campaignItem, ['adgroupList', adgroupIndex, 'optimizationGoal']))"
                      v-slot="{ errors }"
                      name="Target ROAS"
                      :rules="roasBidRules(campaignItem, adgroupIndex)"
                    >
                      <label class="form-label">
                        {{ getNestedValue(campaignItem, ['adgroupList', adgroupIndex, 'optimizationGoal']) === 'VALUE' ? 'Target ROAS' : 'Landing Page View' }}
                      </label>
                      <b-input-group>
                        <b-form-input
                          :value="getNestedValue(campaignItem, ['adgroupList', adgroupIndex, 'roasBid'])"
                          name="roasBid"
                          :disabled="getNestedValue(campaignItem, ['adgroupList', adgroupIndex, 'deepBidType']) !== 'VO_MIN_ROAS'"
                          :placeholder="getNestedValue(campaignItem, ['adgroupList', adgroupIndex, 'deepBidType']) !== 'VO_MIN_ROAS' ? 'No bid' : 'Enter bid value'"
                          style="height: 38px"
                          :state="errors.length ? false : null"
                          @input="handelChangeRoasBid($event, index, adgroupIndex)"
                          @change="handelChangeBid($event, 'roasBid', campaignItem, index, adgroupIndex)"
                        />
                      </b-input-group>
                      <small
                        v-if="getErrorBid(campaignItem, adgroupIndex)"
                        class="text-danger d-block mt-25"
                      >{{ getErrorBid(campaignItem, adgroupIndex) }}</small>
                      <small
                        v-else-if="errors[0]"
                        class="text-danger d-block mt-25"
                      >{{ errors[0] }}</small>
                    </validation-provider>

                    <!-- IN_APP_EVENT/INSTALL/CONVERT/LEAD_GENERATION => conversionBidPrice (Target CPA) -->
                    <validation-provider
                      v-else-if="['IN_APP_EVENT','INSTALL','CONVERT', 'LEAD_GENERATION'].includes(getNestedValue(campaignItem, ['adgroupList', adgroupIndex, 'optimizationGoal']))"
                      v-slot="{ errors }"
                      name="Target CPA"
                      :rules="conversionBidPriceRules(campaignItem, adgroupIndex)"
                    >
                      <label class="form-label">
                        Target CPA
                      </label>
                      <b-input-group>
                        <b-form-input
                          :value="getNestedValue(campaignItem, ['adgroupList', adgroupIndex, 'conversionBidPrice'])"
                          name="conversionBidPrice"
                          :disabled="getNestedValue(campaignItem, ['adgroupList', adgroupIndex, 'bidType']) === 'BID_TYPE_NO_BID'"
                          :placeholder="getNestedValue(campaignItem, ['adgroupList', adgroupIndex, 'bidType']) === 'BID_TYPE_NO_BID' ? 'No bid' : 'Enter bid value'"
                          style="height: 38px"
                          :state="errors.length ? false : null"
                          @input="handelChangeConversionBidPrice($event, index, adgroupIndex)"
                          @change="handelChangeBid($event, 'conversionBidPrice', campaignItem, index, adgroupIndex)"
                        />
                      </b-input-group>
                      <small
                        v-if="getErrorBid(campaignItem, adgroupIndex)"
                        class="text-danger d-block mt-25"
                      >{{ getErrorBid(campaignItem, adgroupIndex) }}</small>
                      <small
                        v-else-if="errors[0]"
                        class="text-danger d-block mt-25"
                      >{{ errors[0] }}</small>
                    </validation-provider>

                    <!-- CLICK => bidPrice (Target CPA) -->
                    <validation-provider
                      v-else-if="['CLICK'].includes(getNestedValue(campaignItem, ['adgroupList', adgroupIndex, 'optimizationGoal']))"
                      v-slot="{ errors }"
                      name="Target CPA"
                      :rules="bidPriceRules(campaignItem, adgroupIndex)"
                    >
                      <label class="form-label">
                        Target CPA
                      </label>
                      <b-input-group>
                        <b-form-input
                          :value="getNestedValue(campaignItem, ['adgroupList', adgroupIndex, 'bidPrice'])"
                          name="bidPrice"
                          :disabled="getNestedValue(campaignItem, ['adgroupList', adgroupIndex, 'bidType']) === 'BID_TYPE_NO_BID'"
                          :placeholder="getNestedValue(campaignItem, ['adgroupList', adgroupIndex, 'bidType']) === 'BID_TYPE_NO_BID' ? 'No bid' : 'Enter bid value'"
                          style="height: 38px"
                          :state="errors.length ? false : null"
                          @input="handelChangeBidPrice($event, index, adgroupIndex)"
                          @change="handelChangeBid($event, 'bidPrice', campaignItem, index, adgroupIndex)"
                        />
                      </b-input-group>
                      <small
                        v-if="getErrorBid(campaignItem, adgroupIndex)"
                        class="text-danger d-block mt-25"
                      >{{ getErrorBid(campaignItem, adgroupIndex) }}</small>
                      <small
                        v-else-if="errors[0]"
                        class="text-danger d-block mt-25"
                      >{{ errors[0] }}</small>
                    </validation-provider>
                    <p class="mb-0 mt-50 font-14 text-placeholder">
                      {{ convertTextBid(adgroup).textDescription }}
                    </p>
                  </b-col>

                  <b-col
                    cols="12"
                    lg="2"
                    md="3"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Budget"
                      :rules="budgetRules(campaignItem, adgroupIndex)"
                    >
                      <label class="form-label">
                        Budget
                      </label>
                      <b-input-group v-if="adgroup.budgetMode !== 'BUDGET_MODE_INFINITE'">
                        <b-form-input
                          :value="getNestedValue(campaignItem, ['adgroupList', adgroupIndex, 'budget'])"
                          name="adgroupBudget"
                          placeholder="Enter Budget"
                          style="height: 38px"
                          :state="errors.length ? false : null"
                          @input="handleChangeBudget($event, index, adgroupIndex)"
                        />
                      </b-input-group>
                      <b-input-group v-else>
                        <b-form-input
                          :value="'Use campaign budget'"
                          disabled
                          name="adgroupBudget"
                          style="height: 38px"
                          :state="errors.length ? false : null"
                        />
                      </b-input-group>
                      <small
                        v-if="errors[0]"
                        class="text-danger d-block mt-25"
                      >{{ errors[0] }}</small>
                      <p class="mb-0 mt-50 font-14 text-placeholder">
                        {{ getBudgetType(adgroup.budgetMode) }}
                      </p>
                    </validation-provider>
                  </b-col>

                  <b-col
                    cols="12"
                    md="3"
                  >
                    <label class="form-label d-flex align-items-center">
                      Schedule Time
                      <feather-icon
                        v-b-tooltip.hover.top.v-light="`Ad delivery is based on your registered account timezone (${etcToUTC(adAccountSelected.timezone)}).`"
                        class="ml-25"
                        icon="HelpCircleIcon"
                        size="13"
                      />
                    </label>
                    <div class="hover-container width-max d-flex align-items-center">
                      <p class="mb-0">
                        {{ formatScheduleTime(adgroup.scheduleStartTime) }} -
                        <span v-if="adgroup.scheduleType === 'SCHEDULE_START_END'">{{ formatScheduleTime(adgroup.scheduleEndTime) }}</span>
                        <span
                          v-else
                          class="text-success"
                        >Ongoing</span>
                      </p>
                      <div
                        v-b-tooltip.hover.top.v-light="'Edit'"
                        class="rounded-circle d-flex align-items-center justify-content-center mx-50"
                        style="background-color: rgba(0, 0, 0, 0.1); width: 24px; height: 24px;"
                      >
                        <feather-icon
                          size="14"
                          class="cursor-pointer"
                          icon="EditIcon"
                          @click="openChangeScheduleTimeModal(index, adgroupIndex)"
                        />
                      </div>
                    </div>
                    <small
                      v-if="getScheduleError(adgroup)"
                      class="text-danger d-block mt-25"
                    >{{ getScheduleError(adgroup) }}</small>
                  </b-col>

                  <b-col
                    cols="12"
                    md="4"
                  >
                    <label class="form-label d-flex align-items-center">
                      Location
                    </label>
                    <div
                      class="hover-container w-100 d-flex align-items-center"
                    >
                      <div
                        v-if="loading"
                        class="d-flex justify-content-center"
                      >
                        <b-spinner
                          variant="secondary"
                          small
                        />
                      </div>
                      <div
                        v-else
                        v-b-tooltip.hover.top.v-light.html
                        class="hover-container d-flex align-items-center"
                        style="max-width: calc(100% - 28px)"
                        :title="getLocationNames(adgroup.locationIds).join(', ')"
                      >
                        <p class="mb-0 wrap-content">
                          {{ getLocationNames(adgroup.locationIds).join(', ') }}
                        </p>
                      </div>
                      <div
                        v-b-tooltip.hover.top.v-light="'Edit'"
                        class="rounded-circle d-flex align-items-center justify-content-center mx-50"
                        style="background-color: rgba(0, 0, 0, 0.1); min-width: 24px; height: 24px;"
                      >
                        <feather-icon
                          size="14"
                          class="cursor-pointer"
                          icon="EditIcon"
                          @click="openChangeLocationModal(index, adgroupIndex)"
                        />
                      </div>
                    </div>
                  </b-col>

                  <b-col
                    cols="12"
                    md="3"
                    class="d-flex flex-nowrap justify-content-end bg-white"
                    style="position: sticky; right: 0; padding-top: 26px; box-shadow: -3px -6px 6px 0px rgba(15, 23, 42, 0.08);"
                  >
                    <!-- Ad List -->
                    <div v-b-tooltip.hover.top.v-light="adgroup.adList.length === 0 ? 'No ads in this group' : ''">
                      <btn-loading
                        variant-convert="btn-submit-outline"
                        pill
                        :disabled="adgroup.adList.length === 0"
                        @click="openChooseVideoModal(index, adgroupIndex)"
                      >
                        Update ad(s) ({{ adgroup.adList.length }})
                      </btn-loading>
                    </div>
                    <div
                      v-b-tooltip.hover.top.v-light="$t('campaign.duplicate')"
                      class="rounded-circle d-flex align-items-center justify-content-center mx-50"
                      style="background-color: rgba(0, 0, 0, 0.1); width: 32px; height: 32px;"
                    >
                      <feather-icon
                        size="21"
                        class="cursor-pointer"
                        icon="CopyIcon"
                        @click="handleDuplicateAdgroup(index, adgroupIndex)"
                      />
                    </div>
                    <div
                      v-b-tooltip.hover.top.v-light="'Delete'"
                      class="rounded-circle d-flex align-items-center justify-content-center"
                      :class="campaignItem.adgroupList.length === 1 ? 'cursor-not-allowed' : 'cursor-pointer'"
                      style="background-color: rgba(225, 28, 78, 0.1); width: 32px; height: 32px;"
                    >
                      <feather-icon
                        icon="Trash2Icon"
                        size="21"
                        class="text-danger"
                        @click="handleDeleteAdGroup(index, adgroupIndex)"
                      />
                    </div>
                  </b-col>
                </b-row>
              </div>

              <small
                v-if="isCbo && errorRoasBid[index]"
                class="text-danger d-block mt-1"
              >{{ errorRoasBid[index] }}</small>
            </b-collapse>
          </b-form>
        </div>
      </validation-observer>
    </div>
    <div
      class="position-sticky bg-white d-flex justify-content-end py-2 pl-2 pr-3"
      style="border-radius: 0 0 28px 28px; bottom: 0; border-top: 1px solid #eaeaea;"
    >
      <btn-loading
        class="mr-1 border-0"
        variant-convert="btn-back"
        pill
        @click="$router.push({ path: '/campaign' })"
      >
        Exit
      </btn-loading>
      <btn-loading
        class="ml-1"
        variant-convert="btn-submit"
        type="submit"
        pill
        :loading="loading"
        @click="handleDuplicateCampaigns"
      >
        Public {{ campaigns.length }} campaign(s)
      </btn-loading>
    </div>

    <choose-video-modal
      ref="open-choose-video-modal"
      :campaigns="campaigns"
    />

    <change-schedule-time-modal
      ref="open-change-schedule-time-modal"
      :campaigns="campaigns"
      :ad-account-selected="adAccountSelected"
      :is-campaign-budget-total="isCampaignBudgetTotal"
    />

    <change-location-modal
      ref="open-change-location-modal"
      :ad-account-selected="adAccountSelected"
      :campaigns="campaigns"
      :location-options="locationOptions"
    />

    <bulk-edit-modal
      ref="open-bulk-edit-modal"
      :campaigns="campaigns"
      :ad-account-selected="adAccountSelected"
      :is-campaign-budget-total="isCampaignBudgetTotal"
      :is-adgroup-budget-total="isAdgroupBudgetTotal"
      @update-campaigns="handleUpdateScheduleTime"
    />

    <bulk-edit-bid-modal
      ref="open-bulk-edit-bid-modal"
      :campaigns="campaigns"
      :ad-account-selected="adAccountSelected"
      @update-campaigns="handleCampaignsUpdate"
    />

    <b-modal
      ref="leavePageModal"
      id="leave-page-modal"
      size="sm"
      hide-header
      centered
      no-close-on-backdrop
      no-close-on-esc
      body-class="pt-1 pb-2"
    >
      <div
        class="d-flex justify-content-between top-modal"
        style="margin-top: 10px;"
      >
        <h4 class="font-medium text-primary">
          Leave site?
        </h4>
      </div>
      <p class="mb-0">
        Changes you made may not be saved.
      </p>
      <!-- Action Buttons -->
      <template #modal-footer>
        <div class="d-flex justify-content-end">
          <btn-loading
            class="mr-1 border-0"
            variant-convert="btn-back"
            pill
            @click="cancelLeave"
          >
            Cancel
          </btn-loading>
          <btn-loading
            class="ml-1"
            variant-convert="btn-submit"
            type="submit"
            pill
            @click="confirmLeave"
          >
            Leave Page
          </btn-loading>
        </div>
      </template>
    </b-modal>
  </div>
</template>
<script>
import { createNamespacedHelpers } from 'vuex'
import get from 'lodash/get'
import BtnLoading from '@/layouts/components/BtnLoading.vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import '@validations'
import DynamicFieldEditor from '@/components/DynamicFieldEditor.vue'
import dynamicFieldEditingMixin from '@/mixins/dynamicFieldEditingMixin'
import {
  VBTooltip,
  VBToggle,
  BForm,
  BFormGroup,
  BRow,
  BCol,
  BInputGroup,
  BFormInput,
  BSpinner,
  BCollapse,
  BDropdown,
  BDropdownItem, BModal,
} from 'bootstrap-vue'
import dayjs from 'dayjs'
import ChooseVideoModal from '@/views/campaign/components/duplicate/chooseVideo/ChooseVideoModal.vue'
import ChangeScheduleTimeModal from '@/views/campaign/components/duplicate/ChangeScheduleTimeModal.vue'
import { toastification } from '@core/mixins/toast'
import Ripple from 'vue-ripple-directive'
import BulkEditModal from '@/views/campaign/components/duplicate/BulkEditModal.vue'
import campaignMixin from '@/mixins/campaignMixin'
import BulkEditBidModal from '@/views/campaign/components/duplicate/BulkEditBidModal.vue'
import numberFormatMixin from '@/mixins/numberFormatMixin'
import { BUDGET_MODE } from '@/constants'
import ChangeLocationModal from '@/views/campaign/components/duplicate/ChangeLocationModal.vue'

const { mapActions, mapGetters } = createNamespacedHelpers('campaign')
const { mapGetters: adAccountsGetters, mapActions: adAccountsActions } = createNamespacedHelpers('adsAccount')
export default {
  components: {
    ChangeLocationModal,
    BulkEditBidModal,
    BModal,
    BulkEditModal,
    BSpinner,
    BFormInput,
    BInputGroup,
    BForm,
    BFormGroup,
    BRow,
    BCol,
    BCollapse,
    BDropdown,
    BDropdownItem,
    ChooseVideoModal,
    BtnLoading,
    ValidationProvider,
    ValidationObserver,
    ChangeScheduleTimeModal,
    DynamicFieldEditor,
  },

  directives: {
    'b-tooltip': VBTooltip,
    'b-toggle': VBToggle,
    Ripple,
  },

  mixins: [dynamicFieldEditingMixin, toastification, campaignMixin, numberFormatMixin],

  data() {
    return {
      dayjs,
      campaigns: [],
      isLoadingLocation: false,
      initLoading: false,
      collapseStates: { 0: true }, // Track collapse state for each campaign

      errorRoasBid: {},

      unsaved: false,
      shouldConfirmLeave: true,
      allLocations: [],
    }
  },

  computed: {
    ...mapGetters(['campaignDetail', 'listAdGroupCampaign', 'listAdCampaignSpp', 'listAdCampaignSp', 'listAdCampaignManual', 'listLocation', 'status', 'message', 'loading']),
    ...adAccountsGetters(['listAccount']),
    adAccountSelected() {
      return get(this.listAccount, ['content'], [])[0]
    },

    locationOptions() {
      return this.listLocation?.map(country => ({
        id: country?.location_id,
        label: country?.name,
        children: country.children ? country?.children?.map(province => ({
          id: province?.location_id,
          label: province?.name,
          children: province?.children ? province?.children?.map(city => ({
            id: city?.location_id,
            label: city?.name,
          })) : [],
        })) : [],
      }))
    },

    getLocationNames() {
      return locationIds => {
        const names = []
        locationIds.forEach(id => {
          const location = this.allLocations?.find(loc => loc.location_id === id)
          if (location) {
            names.push(location.name)
          } else {
            this.allLocations.forEach(loc => {
              const province = loc?.children?.find(prov => prov.location_id === id)
              if (province) {
                names.push(province.name)
              } else {
                loc.children.forEach(prov => {
                  const city = prov?.children?.find(cty => cty.location_id === id)
                  if (city) {
                    names.push(city.name)
                  }
                })
              }
            })
          }
        })
        return names
      }
    },

    campaign() {
      return get(this.campaignDetail, ['content'], [])[0]
    },

    isCampaignBudgetTotal() {
      return this.campaign?.budget_mode === BUDGET_MODE.BUDGET_MODE_TOTAL && this.campaign?.budget_optimize_on === true
    },

    listAdGroup() {
      return get(this.listAdGroupCampaign, ['content'], [])
    },

    isAdGroupSameBudgetMode() {
      const firstBudget = this.listAdGroup?.[0]?.budget_mode
      return this.listAdGroup?.every(adgroup => adgroup.budget_mode === firstBudget)
    },

    isAdgroupBudgetTotal() {
      return !!this.listAdGroup?.find(item => item.budget_mode === BUDGET_MODE.BUDGET_MODE_TOTAL)
    },

    listAd() {
      if (this.getCampaignType(this.campaign) === 'S++') {
        return get(this.listAdCampaignSpp, ['content'], [])
      } if (this.getCampaignType(this.campaign) === 'S+') {
        return get(this.listAdCampaignSp, ['content'], [])
      }
      return get(this.listAdCampaignManual, ['content'], [])
    },

    isCbo() {
      return this.campaign?.budget_optimize_on === true
    },

    formatScheduleTime() {
      return value => (value ? dayjs(value).format('YYYY-MM-DD') : '')
    },
  },

  watch: {
    $route: {
      immediate: true,
      async handler(val) {
        if (val && val.query?.campaignDuplicate) {
          this.initLoading = true
          const params = {
            advertiserId: val.query?.advertiserId,
            campaignIds: val.query?.campaignDuplicate,
            page: 1,
            size: 100,
          }
          await this.getCampaignDetail(params)
          await this.getListAdGroupCampaign(params)
          if (this.getCampaignType(this.campaign) === 'S++') {
            await this.getListAdCampaignSpp(params)
          } else if (this.getCampaignType(this.campaign) === 'S+') {
            await this.getListAdCampaignSp(params)
          } else {
            await this.getListAdCampaignManual(params)
          }
          this.setupCampaigns(val.query)
          this.initLoading = false
        }
      },
    },
  },

  async created() {
    await this.getAccountList({ keyword: this.$route.query?.advertiserId })
  },

  mounted() {
    // eslint-disable-next-line no-underscore-dangle
    this._onBeforeUnload = e => {
      if (this.shouldConfirmLeave) {
        e.preventDefault()
        e.returnValue = ''
      }
      return undefined
    }
    // eslint-disable-next-line no-underscore-dangle
    window.addEventListener('beforeunload', this._onBeforeUnload)
  },
  beforeDestroy() {
    // eslint-disable-next-line no-underscore-dangle
    window.removeEventListener('beforeunload', this._onBeforeUnload)
  },

  beforeRouteLeave(to, from, next) {
    if (to.path === '/create-campaign-video') {
      next()
      return
    }
    this.pendingNavigation = next
    if (!this.isConfirm) {
      this.$refs.leavePageModal.show()
    } else {
      this.pendingNavigation()
      this.pendingNavigation = null
    }
  },

  methods: {
    ...mapActions(['getCampaignDetail', 'getListAdGroupCampaign', 'getListAdCampaignSpp', 'getListAdCampaignSp', 'getListAdCampaignManual', 'duplicateCampaigns', 'getListLocation']),
    ...adAccountsActions(['getAccountList']),

    roasBidRules(campaign, adgroupIndex) {
      const isVoMinRoas = this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'deepBidType']) === 'VO_MIN_ROAS'
      if (!isVoMinRoas) return ''
      if (this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'optimizationEvent']) === 'AD_REVENUE_VALUE') {
        return 'required|between:0.001,10'
      }
      if (this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'optimizationEvent']) === 'ACTIVE_PAY') {
        if (this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'vboWindow']) === 'ZERO_DAY') {
          return 'required|between:0.001,1000'
        } if (this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'vboWindow']) === 'SEVEN_DAYS') {
          return 'required|between:0.01,1000'
        }
      }
      return 'required'
    },

    conversionBidPriceRules(campaign, adgroupIndex) {
      const isNoBid = this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'bidType']) === 'BID_TYPE_NO_BID'
      if (isNoBid) return ''
      const max = this.isCbo ? campaign?.budget : this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'budget'])
      return `required|between:0.01,${Number(max) - 0.01}`
    },

    bidPriceRules(campaign, adgroupIndex) {
      const isNoBid = this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'bidType']) === 'BID_TYPE_NO_BID'
      if (isNoBid) return ''
      const budget = this.isCbo ? campaign?.budget : this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'budget'])
      const max = Number(budget) - 0.01 > 29.99 ? 29.99 : Number(budget) - 0.01
      return `required|between:0.01,${max}`
    },

    getErrorBid(campaign, adgroupIndex) {
      const isVoMinRoas = this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'deepBidType']) === 'VO_MIN_ROAS'
      if (this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'deepBidType']) && !isVoMinRoas) return ''
      const isNoBid = this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'bidType']) === 'BID_TYPE_NO_BID'
      if (this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'bidType']) && isNoBid) return ''
      if (!campaign?.budget && !this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'budget'])) {
        return 'The budget is required'
      }

      // Dynamically validate Target CPA against current budget without needing field interaction
      const optimizationGoal = this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'optimizationGoal'])
      const adgroupBudget = this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'budget'])
      const budget = this.isCbo ? campaign?.budget : adgroupBudget

      if (!budget || isNoBid) return ''

      const minVal = 0.01

      if (['IN_APP_EVENT', 'INSTALL', 'CONVERT', 'LEAD_GENERATION'].includes(optimizationGoal)) {
        const value = Number(this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'conversionBidPrice']))
        const max = Number(budget) - 0.01
        if (!Number.isFinite(value)) return ''
        if (value < minVal || value > max) return `The Target CPA field must be between ${minVal} and ${max}`
      }

      if (optimizationGoal === 'CLICK') {
        const value = Number(this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'bidPrice']))
        const rawMax = Number(budget) - 0.01
        const max = rawMax > 29.99 ? 29.99 : rawMax
        if (!Number.isFinite(value)) return ''
        if (value < minVal || value > max) return `The Target CPA field must be between ${minVal} and ${max}`
      }

      return ''
    },

    budgetRules(campaign, adgroupIndex) {
      const isInfiniteBudget = this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'budgetMode']) === BUDGET_MODE.BUDGET_MODE_INFINITE
      if (isInfiniteBudget) return ''

      const isTotalBudget = this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'budgetMode']) === BUDGET_MODE.BUDGET_MODE_TOTAL

      if (isTotalBudget) {
        const scheduleStartTime = this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'scheduleStartTime'])
        const scheduleEndTime = this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'scheduleEndTime'])

        if (scheduleStartTime && scheduleEndTime) {
          const startDate = dayjs(scheduleStartTime)
          const endDate = dayjs(scheduleEndTime)

          let totalDays = endDate.diff(startDate, 'day')

          const startTimeOnly = startDate.format('HH:mm:ss')
          const endTimeOnly = endDate.format('HH:mm:ss')

          if (startTimeOnly !== endTimeOnly) {
            totalDays += 1
          }

          return `required|between:${20 * totalDays},9999999`
        }
      }
      return 'required|between:20,9999999'
    },

    openChooseVideoModal(campaignIndex, adgroupIndex) {
      this.$refs['open-choose-video-modal'].showModal(campaignIndex, adgroupIndex)
    },

    openChangeScheduleTimeModal(campaignIndex, adgroupIndex) {
      this.$refs['open-change-schedule-time-modal'].showModal(campaignIndex, adgroupIndex)
    },

    async openChangeLocationModal(campaignIndex, adgroupIndex) {
      const placements = this.campaigns[campaignIndex]?.adgroupList[adgroupIndex].placements
      this.$refs['open-change-location-modal'].showModal(campaignIndex, adgroupIndex)
      await this.getListLocation({ advertiserId: this.$route.query?.advertiserId, placements: JSON.stringify(placements), objectiveType: this.campaign.objective_type })
    },

    openBulkEditModal(type) {
      this.$refs['open-bulk-edit-modal'].showModal(type)
    },

    openBulkEditBidModal(type) {
      this.$refs['open-bulk-edit-bid-modal'].showModal(type)
    },

    getScheduleError(adgroup) {
      const tz = this.adAccountSelected.timezone
      const start = adgroup?.scheduleStartTime ? dayjs.tz(adgroup.scheduleStartTime, tz) : null
      const end = adgroup?.scheduleEndTime ? dayjs.tz(adgroup.scheduleEndTime, tz) : null
      const nowTz = dayjs().tz(tz)

      if (start && start.isBefore(nowTz)) {
        return "Start time can't be earlier than the current time"
      }
      if (start && end && !start.isBefore(end)) {
        return 'Start date must be earlier than end date.'
      }
      return ''
    },

    normalizeAdSpp(ad) {
      return {
        adId: ad.smart_plus_ad_id,
        adName: ad.ad_name,
        ...(ad?.landing_page_url_list?.length > 0 && { landingPageUrl: ad?.landing_page_url_list?.[0]?.landing_page_url }),
        creativeList: (ad.creative_list || []).map(creative => ({
          ...creative,
          ad_text: ad?.ad_text_list?.map(item => item?.ad_text),
          creative_info: {
            ...creative.creative_info,
            ad_text: ad?.ad_text_list?.map(item => item?.ad_text),
            ad_format: creative.creative_info?.ad_format,
            identity_authorized_bc_id: creative.creative_info?.identity_authorized_bc_id,
            identity_id: creative.creative_info?.identity_id,
            identity_type: creative.creative_info?.identity_type,
            item_id: creative.creative_info?.tiktok_item_id,
            video_info: {
              ...creative?.creative_info?.video_info,
              file_name: creative?.creative_info?.video_info?.file_name,
              video_id: creative?.creative_info?.video_info?.video_id,
            },
          },
        })),
      }
    },

    normalizeAdSp(ad) {
      return {
        adId: ad.ad_id,
        adName: `Ad name ${dayjs(ad.create_time).format('YYYY-MM-DD HH:mm:ss')}`,
        ...(ad?.landing_page_urls?.length > 0 && { landingPageUrl: ad?.landing_page_urls?.[0]?.landing_page_url }),
        creativeList: ad.media_info_list.map(creative => ({
          // adMaterialId: creative.ad_material_id,
          ...creative,
          ad_text: ad?.title_list?.map(item => item?.title),
          creative_info: {
            ...creative.media_info,
            ad_text: ad?.title_list?.map(item => item?.title),
            identity_authorized_bc_id: creative.media_info?.identity_authorized_bc_id,
            identity_id: creative.media_info?.identity_id,
            identity_type: creative.media_info?.identity_type,
            item_id: creative.media_info?.tiktok_item_id,
            video_info: {
              ...creative?.media_info?.video_info,
              file_name: creative?.media_info?.video_info?.file_name,
              video_id: creative?.media_info?.video_info?.video_id,
            },
          },
        })),
      }
    },

    normalizeAdManual(ad) {
      return {
        adId: ad.ad_id,
        adName: ad.ad_name,
        ...(ad?.landing_page_url && { landingPageUrl: ad?.landing_page_url }),
        creativeList: [{
          ad_text: [ad?.ad_text],
          creative_info: {
            ad_text: [ad?.ad_text],
            ad_format: ad?.ad_format,
            identity_authorized_bc_id: ad?.identity_authorized_bc_id,
            identity_id: ad?.identity_id,
            identity_type: ad?.identity_type,
            ...((!ad?.video_id || !ad?.image_ids || !ad?.ad_text) && ad?.tiktok_item_id && { item_id: ad?.tiktok_item_id }),
            material_operation_status: ad?.material_operation_status,
            image_info: ad?.image_ids.map(item => ({ web_uri: item })),
            video_info: {
              ...ad?.video_info,
              file_name: ad?.video_id,
              video_id: ad?.video_id,
            },
          },
        }],
      }
    },

    async setupCampaigns(queryParams = {}) {
      this.isLoadingLocation = true
      const campaignsStorage = JSON.parse(localStorage.getItem('campaigns'))
      if (!!campaignsStorage && campaignsStorage[0].campaignId === queryParams.campaignDuplicate && !this.$route.query?.isNew) {
        this.campaigns = campaignsStorage
        this.errorRoasBid = {}
      } else {
        delete this.$route.query?.isNew
        const listCampaignName = queryParams?.listCampaignName?.split(',-,') || []
        const convertListAdGroup = this.listAdGroup?.map(item => {
          if (this.getCampaignType(this.campaign) === 'S++' || this.getCampaignType(this.campaign) === 'Manual') {
            const adsInGroup = this.listAd.filter(ad => ad.adgroup_id === item.adgroup_id)
            return {
              ...item,
              ads: adsInGroup,
            }
          }
          return {
            ...item,
            ads: this.listAd,
          }
        })

        this.campaigns = Array.from({ length: listCampaignName.length }, (_, index) => ({
          campaignId: queryParams?.campaignDuplicate || '',
          campaignName: listCampaignName[index] || '',
          budgetMode: this.campaign?.budget_mode,
          budget: this.campaign?.budget,
          adgroupList: convertListAdGroup?.map(item => ({
            placements: item.placements,
            adgroupId: item.adgroup_id,
            adgroupName: item.adgroup_name,
            budget: item?.budget,
            budgetMode: item.budget_mode,
            locationIds: item.location_ids,
            scheduleType: item.schedule_type,
            scheduleStartTime: dayjs.utc(item.schedule_start_time).tz(this.adAccountSelected.timezone).format('YYYY-MM-DD HH:mm'),
            scheduleEndTime: dayjs.utc(item.schedule_end_time).tz(this.adAccountSelected.timezone).format('YYYY-MM-DD HH:mm'),
            optimizationGoal: item?.optimization_goal,
            optimizationEvent: item?.optimization_event,
            vboWindow: item?.vbo_window,
            ...(item.deep_bid_type && { deepBidType: item.deep_bid_type }),
            ...(item.bid_type && { bidType: item.bid_type }),
            ...(item.roas_bid && { roasBid: item.roas_bid }),
            ...(item.conversion_bid_price && { conversionBidPrice: item.conversion_bid_price }),
            ...(item.bid_price && { bidPrice: item.bid_price }),
            adList: item.ads.map(ad => {
              if (this.getCampaignType(this.campaign) === 'S++') {
                return this.normalizeAdSpp(ad)
              } if (this.getCampaignType(this.campaign) === 'S+') {
                return this.normalizeAdSp(ad)
              }
              return this.normalizeAdManual(ad)
            }),
          })),
        }))

        await this.getListLocation({ advertiserId: this.$route.query?.advertiserId, placements: JSON.stringify(['PLACEMENT_TIKTOK', 'PLACEMENT_PANGLE', 'PLACEMENT_GLOBAL_APP_BUNDLE']), objectiveType: this.campaign.objective_type })
        if (this.status) {
          this.allLocations = this.listLocation
          this.isLoadingLocation = false
        }

        this.errorRoasBid = {}

        if (this.$route && this.$route.query?.campaignIndex && this.$route.query?.adgroupIndex) {
          this.openChooseVideoModal(this.$route.query?.campaignIndex, this.$route.query?.adgroupIndex)
        }
      }
    },

    validBid(fieldKey, campaignIndex) {
      this.$set(this.errorRoasBid, campaignIndex, '')
      if (this.isCbo) {
        const adgroups = this.campaigns[campaignIndex].adgroupList
        const firstValue = adgroups[0]?.[fieldKey]
        const allSame = adgroups.every(adgroup => Number(adgroup[fieldKey]) === Number(firstValue))
        if (!allSame) {
          this.$set(this.errorRoasBid, campaignIndex, 'Because you\'ve selected a campaign budget strategy, your Target ROAS must be the same across ad groups.')
        } else {
          this.$set(this.errorRoasBid, campaignIndex, '')
        }
      }
    },

    async handleChangeBudget(value, campaignIndex, adgroupIndex) {
      this.updateFieldValue(campaignIndex, ['adgroupList', adgroupIndex, 'budget'], value)

      await this.$nextTick()
      await this.$refs.campaignSetupValidation.validate()
    },

    // Generic handler to update a bid field and ensure CBO consistency
    handleBidFieldChange(fieldKey, value, campaignIndex, adgroupIndex) {
      this.updateFieldValue(campaignIndex, ['adgroupList', adgroupIndex, fieldKey], value)

      this.validBid(fieldKey, campaignIndex)
    },

    handelChangeRoasBid(value, campaignIndex, adgroupIndex) {
      this.handleBidFieldChange('roasBid', value, campaignIndex, adgroupIndex)
    },

    handelChangeConversionBidPrice(value, campaignIndex, adgroupIndex) {
      this.handleBidFieldChange('conversionBidPrice', value, campaignIndex, adgroupIndex)
    },

    handelChangeBidPrice(value, campaignIndex, adgroupIndex) {
      this.handleBidFieldChange('bidPrice', value, campaignIndex, adgroupIndex)
    },

    handelChangeBid(value, type, campaign, campaignIndex, adgroupIndex) {
      let bid = value
      if (this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'optimizationEvent']) === 'AD_REVENUE_VALUE' || (this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'optimizationEvent']) === 'ACTIVE_PAY' && this.getNestedValue(campaign, ['adgroupList', adgroupIndex, 'vboWindow']) === 'ZERO_DAY')) {
        bid = (Math.round(value * 1000) / 1000).toFixed(3)
      } else {
        bid = (Math.round(value * 100) / 100).toFixed(2)
      }
      this.handleBidFieldChange(type, bid, campaignIndex, adgroupIndex)
    },

    getBidFieldName(optimizationGoal) {
      if (optimizationGoal === 'VALUE') return 'roasBid'
      if (['IN_APP_EVENT', 'INSTALL', 'CONVERT', 'LEAD_GENERATION'].includes(optimizationGoal)) return 'conversionBidPrice'
      if (optimizationGoal === 'CLICK') return 'bidPrice'
      return null
    },

    handleCampaignsUpdate(updatedCampaigns) {
      this.campaigns = updatedCampaigns
      const fieldKey = this.getBidFieldName(this.campaigns[0]?.adgroupList[0]?.optimizationGoal)
      this.campaigns.forEach((campaign, campaignIndex) => {
        this.validBid(fieldKey, campaignIndex)
      })
    },

    handleUpdateScheduleTime(updatedCampaigns) {
      this.campaigns = updatedCampaigns
    },

    handleDeleteCampaign(index) {
      if (this.campaigns.length === 1) return
      this.campaigns.splice(index, 1)
    },

    handleDeleteAdGroup(campaignIndex, adGroupIndex) {
      if (this.campaigns[campaignIndex].adgroupList.length === 1) return
      this.campaigns[campaignIndex].adgroupList.splice(adGroupIndex, 1)
    },

    async handleDuplicateAdgroup(campaignIndex, adGroupIndex) {
      const source = this.campaigns[campaignIndex].adgroupList[adGroupIndex]
      const adGroupNew = JSON.parse(JSON.stringify(source))
      adGroupNew.adgroupName = `Copy of ${source.adgroupName}`
      this.campaigns[campaignIndex].adgroupList.push(adGroupNew)

      await this.$nextTick()
      await this.$refs.campaignSetupValidation.validate()
    },

    async handleDuplicateCampaigns() {
      const isFormValid = await this.$refs.campaignSetupValidation.validate()
      const hasRoasError = Object.values(this.errorRoasBid).some(error => !!error)
      const isScheduleValid = this.campaigns.every(campaign => campaign.adgroupList.every(adgroup => !this.getScheduleError(adgroup)))

      if (!isFormValid || hasRoasError || !isScheduleValid) {
        return
      }

      const params = {
        advertiserId: this.$route.query?.advertiserId,
        campaignList: this.campaigns.map(campaign => ({
          ...campaign,
          ...(campaign.budget && { budget: Number(campaign.budget) }),
          adgroupList: campaign.adgroupList.map(adgroup => ({
            ...adgroup,
            ...(adgroup.roasBid && { roasBid: Number(adgroup.roasBid) }),
            ...(adgroup.conversionBidPrice && { conversionBidPrice: Number(adgroup.conversionBidPrice) }),
            ...(adgroup.bidPrice && { bidPrice: Number(adgroup.bidPrice) }),
            ...(adgroup.budget && { budget: Number(adgroup.budget) }),
            ...(adgroup.locationIds && { targetingSpec: { locationIds: adgroup.locationIds } }),

            scheduleStartTime: dayjs.tz(adgroup.scheduleStartTime, this.adAccountSelected.timezone).utc().format('YYYY-MM-DD HH:mm:ss'),
            scheduleEndTime: dayjs.tz(adgroup.scheduleEndTime, this.adAccountSelected.timezone).utc().format('YYYY-MM-DD HH:mm:ss'),
            adList: adgroup.adList.map(ad => ({
              adId: ad?.adId,
              adName: ad?.adName,
              ...(ad?.landingPageUrl && { landingPageUrlList: [{ landingPageUrl: ad?.landingPageUrl }] }),
              ...(ad?.creativeList.find(item => !item?.creative_info?.item_id) && { adTextList: ad?.creativeList?.find(item => !item?.creative_info?.item_id)?.ad_text?.map(text => ({ adText: text })) }),
              creativeList: ad.creativeList.map(creative => ({
                creativeInfo: {
                  adFormat: creative?.creative_info?.ad_format || 'SINGLE_VIDEO',
                  identityAuthorizedBcId: creative?.creative_info?.identity_authorized_bc_id || '',
                  identityId: creative?.creative_info.identity_id || '',
                  identityType: creative?.creative_info.identity_type || '',
                  ...(creative?.creative_info?.item_id && { tiktokItemId: creative?.creative_info?.item_id }),
                  ...(creative?.creative_info?.image_info && creative?.creative_info?.image_info?.length > 0 && { imageInfo: creative?.creative_info?.image_info.map(item => ({ webUri: item.web_uri })) }),
                  ...(creative?.creative_info?.video_info?.video_id && {
                    videoInfo: {
                      fileName: creative?.creative_info?.video_info?.file_name,
                      videoId: creative?.creative_info?.video_info?.video_id,
                    },
                  }),
                },
                ...(creative.material_operation_status && { materialOperationStatus: creative.material_operation_status }),
              })),
            })),
          })),
        })),
      }
      await this.duplicateCampaigns(params)
      if (this.status) {
        this.isConfirm = true
        this.shouldConfirmLeave = false // Disable confirmation on successful submit
        this.$router.push({ path: '/campaign' })
      } else {
        this.toastFailure(this.message)
      }
    },

    confirmLeave() {
      if (this.pendingNavigation) {
        this.pendingNavigation()
        this.pendingNavigation = null
        localStorage.removeItem('campaigns')
      }
    },
    cancelLeave() {
      if (this.pendingNavigation) {
        this.pendingNavigation(false)
        this.pendingNavigation = null
        this.$refs.leavePageModal.hide()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.wrap-content {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}</style>
